import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  EyeOutlined,
  PlusOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  getSketchDesignAccumulationsDeviationsAll,
  getSketchDesignAccumulationsReservesAll,
} from "../../../../api/sketchDesignAccumulationsApi";
import "./EpAccumulatedDeviations.css";

const formatTenge = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  return `₸ ${n.toLocaleString("ru-RU")}`;
};

const mergeRows = (deviationsList, reservesList) => {
  const map = new Map();

  for (const item of deviationsList || []) {
    const id = item.project?.id;
    if (id == null) continue;
    map.set(id, {
      project: item.project,
      deviations: Number(item.deviations) || 0,
      reserve: 0,
    });
  }

  for (const item of reservesList || []) {
    const id = item.project?.id;
    if (id == null) continue;
    const reserve = Number(item.reserve) || 0;
    const existing = map.get(id);
    if (existing) {
      existing.reserve = reserve;
    } else {
      map.set(id, {
        project: item.project,
        deviations: 0,
        reserve,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) =>
    (a.project?.name || "").localeCompare(b.project?.name || "", "ru")
  );
};

const selectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: "8px",
    borderColor: "#dee2e6",
    minHeight: "40px",
    fontFamily: "Manrope, sans-serif",
    fontSize: "14px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function EpAccumulatedDeviations() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [projectFilter, setProjectFilter] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [deviations, reserves] = await Promise.all([
        getSketchDesignAccumulationsDeviationsAll(),
        getSketchDesignAccumulationsReservesAll(),
      ]);
      setRows(mergeRows(deviations, reserves));
    } catch (e) {
      console.error(e);
      setError(e.message || "Не удалось загрузить данные");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const computedRows = useMemo(() => {
    return rows.map((row) => {
      const deviationsTotal = row.deviations;
      const balance = row.reserve;
      const spent = Math.max(0, deviationsTotal - balance);
      return {
        ...row,
        deviationsTotal,
        spent,
        balance,
      };
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!projectFilter) return computedRows;
    return computedRows.filter((r) => r.project?.id === projectFilter.value);
  }, [computedRows, projectFilter]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => ({
        deviations: acc.deviations + r.deviationsTotal,
        spent: acc.spent + r.spent,
        balance: acc.balance + r.balance,
      }),
      { deviations: 0, spent: 0, balance: 0 }
    );
  }, [filteredRows]);

  const spentPercent =
    totals.deviations > 0
      ? ((totals.spent / totals.deviations) * 100).toFixed(1).replace(".", ",")
      : "0";

  const projectOptions = useMemo(() => {
    const opts = computedRows.map((r) => ({
      value: r.project?.id,
      label: r.project?.name || `Проект #${r.project?.id}`,
    }));
    return [{ value: null, label: "Все" }, ...opts];
  }, [computedRows]);

  const openFinance = (projectId) => {
    navigate(`/projects/sketch-design/finance/${projectId}`);
  };

  if (loading) {
    return (
      <div className="ep-deviations">
        <div className="ep-deviations__state">Загрузка...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ep-deviations">
        <div className="ep-deviations__state ep-deviations__state--error">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="ep-deviations">
      <h1 className="ep-deviations__title">Накопленные отклонения ЭП</h1>

      <div className="ep-deviations__filter">
        <label htmlFor="ep-deviations-project-select">Проект</label>
        <Select
          inputId="ep-deviations-project-select"
          classNamePrefix="ep-deviations-select"
          options={projectOptions}
          value={
            projectFilter ||
            projectOptions.find((o) => o.value === null) ||
            null
          }
          onChange={(opt) => setProjectFilter(opt?.value === null ? null : opt)}
          styles={selectStyles}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          placeholder="Все"
          noOptionsMessage={() => "Нет проектов"}
        />
      </div>

      <div className="ep-deviations__kpis">
        <div className="ep-deviations__card">
          <div className="ep-deviations__card-icon ep-deviations__card-icon--blue">
            <WalletOutlined style={{ color: "#2563eb", fontSize: "22px" }} />
          </div>
          <div className="ep-deviations__card-body">
            <div className="ep-deviations__card-label">Всего отклонений</div>
            <div className="ep-deviations__card-value">
              {formatTenge(totals.deviations)}
            </div>
            <div className="ep-deviations__card-hint">
              Всего отклонений по проектам ЭП
            </div>
          </div>
        </div>
        <div className="ep-deviations__card">
          <div className="ep-deviations__card-icon ep-deviations__card-icon--red">
            <ArrowDownOutlined style={{ color: "#dc2626", fontSize: "22px" }} />
          </div>
          <div className="ep-deviations__card-body">
            <div className="ep-deviations__card-label">Израсходовано</div>
            <div className="ep-deviations__card-value">
              {formatTenge(totals.spent)}
            </div>
            <div className="ep-deviations__card-hint">
              {spentPercent}% от общей суммы
            </div>
          </div>
        </div>
        <div className="ep-deviations__card">
          <div className="ep-deviations__card-icon ep-deviations__card-icon--green">
            <ArrowUpOutlined style={{ color: "#059669", fontSize: "22px" }} />
          </div>
          <div className="ep-deviations__card-body">
            <div className="ep-deviations__card-label">Остаток</div>
            <div className="ep-deviations__card-value ep-deviations__card-value--balance">
              {formatTenge(totals.balance)}
            </div>
            <div className="ep-deviations__card-hint">
              Доступно к использованию
            </div>
          </div>
        </div>
      </div>

      <div className="ep-deviations__table-wrap">
        <table className="ep-deviations__table">
          <thead>
            <tr>
              <th className="ep-deviations__col-project">Проекты</th>
              <th>Сумма отклонений</th>
              <th>Израсходовано</th>
              <th>Доступный остаток</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={5} className="ep-deviations__state">
                  Нет данных для отображения
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr key={row.project?.id}>
                  <td className="ep-deviations__col-project">
                    {row.project?.name || "—"}
                  </td>
                  <td>{formatTenge(row.deviationsTotal)}</td>
                  <td>
                    {row.spent > 0 ? (
                      formatTenge(row.spent)
                    ) : (
                      <span className="ep-deviations__dash">—</span>
                    )}
                  </td>
                  <td>{formatTenge(row.balance)}</td>
                  <td>
                    <div className="ep-deviations__actions">
                      <button
                        type="button"
                        className="ep-deviations__action-btn"
                        title="Открыть финансы проекта"
                        aria-label="Открыть финансы проекта"
                        onClick={() => {
                          if (row.project?.id != null)
                            openFinance(row.project.id);
                        }}
                      >
                        <EyeOutlined />
                      </button>
                      <button
                        type="button"
                        className="ep-deviations__action-btn"
                        title="Перейти в финансы проекта"
                        aria-label="Перейти в финансы проекта"
                        onClick={() => {
                          if (row.project?.id != null)
                            openFinance(row.project.id);
                        }}
                      >
                        <PlusOutlined />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {filteredRows.length > 0 && (
            <tfoot>
              <tr>
                <td>ИТОГО</td>
                <td>{formatTenge(totals.deviations)}</td>
                <td>{formatTenge(totals.spent)}</td>
                <td>{formatTenge(totals.balance)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

export default EpAccumulatedDeviations;
